/**
 * Meta Pixel (a.k.a. Meta "dataset") helper.
 *
 * Loads the Meta Pixel once and exposes thin wrappers for the events we care
 * about. Everything is a NO-OP until REACT_APP_META_PIXEL_ID is set in the
 * environment (Vercel). This means the code can ship and deploy safely BEFORE
 * the Pixel ID exists — nothing fires and nothing breaks. The moment the env
 * var is added and the app is redeployed, tracking activates automatically with
 * no further code change.
 *
 * PageView is fired by the router on every route change (see App.jsx), NOT here,
 * so single-page navigations are counted and the first page load is not
 * double-counted.
 *
 * To activate: set REACT_APP_META_PIXEL_ID to the 15–16 digit Pixel ID in
 * Vercel (Project → Settings → Environment Variables) and redeploy.
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

let initialised = false;

// Official Meta base loader, trimmed to just define window.fbq and inject
// fbevents.js. Meta's own guard (if (f.fbq) return) prevents double injection.
function loadPixelScript() {
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

// True only when a Pixel ID is configured AND fbq is present on the page.
function ready() {
  return Boolean(PIXEL_ID) && typeof window !== 'undefined' && typeof window.fbq === 'function';
}

// Load + init the pixel exactly once. Dormant (returns early) until a Pixel ID
// is configured, so it is safe to call unconditionally on app start.
export function initMetaPixel() {
  if (initialised) return;
  if (!PIXEL_ID) return;
  if (typeof window === 'undefined') return;
  loadPixelScript();
  window.fbq('init', PIXEL_ID);
  initialised = true;
}

// Fire on every route change (SPA navigations do not reload the page, so Meta
// would otherwise only ever see the first URL).
export function trackPageView() {
  if (!ready()) return;
  window.fbq('track', 'PageView');
}

// Standard event: a new free-trial account was created. This is the primary
// conversion event to optimise B2C ad campaigns toward early on.
export function trackCompleteRegistration(params = {}) {
  if (!ready()) return;
  window.fbq('track', 'CompleteRegistration', params);
}

// Standard event: a paid entitlement was confirmed active.
// NOTE: `value` is intentionally not set here because the paid SAR amount is not
// present in the entitlement payload on the success page. If you later expose
// the amount (e.g. from the Stripe checkout session), pass { value, currency }
// so Meta can optimise toward revenue rather than just purchase count.
export function trackPurchase(params = {}) {
  if (!ready()) return;
  window.fbq('track', 'Purchase', params);
}
